import { updateCheckoutSession } from '@otter-ai/recall-client';
import {
  CheckoutSessionActionType,
  PurchaseCheckoutSessionUpdateEvent,
  trackEvent,
} from '@otter-ai/telemetry';
import { useCheckout } from '@stripe/react-stripe-js';
import { useMutation } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import { useEffect } from 'react';
import { trialDaysIsUpdatingAtom } from '#atoms';

type UpdateTrialDaysParams = {
  trialDays: number | string;
};

export const useUpdateTrialDays = () => {
  const { runServerUpdate, id } = useCheckout();
  const setIsTrialDaysUpdating = useSetAtom(trialDaysIsUpdatingAtom);

  const updateTrialDaysMutation = useMutation<
    boolean,
    Error,
    UpdateTrialDaysParams
  >({
    mutationFn: async ({ trialDays }: UpdateTrialDaysParams) => {
      await runServerUpdate(() =>
        updateCheckoutSession({
          body: {
            checkout_session_id: id,
            session_params: {
              subscription_data: { trial_period_days: trialDays },
            },
          },
          throwOnError: true,
        }),
      );

      return true;
    },
    onSuccess: (__, variables) => {
      trackEvent(
        PurchaseCheckoutSessionUpdateEvent({
          Status: 'success',
          CheckoutSessionActionType: CheckoutSessionActionType.ChangeTrialDays,
          CouponTrialDays: Number(variables.trialDays),
        }),
      );
    },
    onError: (error, variables) => {
      trackEvent(
        PurchaseCheckoutSessionUpdateEvent({
          Status: 'failure',
          ErrorMessage: error.message,
          CheckoutSessionActionType: CheckoutSessionActionType.ChangeTrialDays,
          CouponTrialDays: Number(variables.trialDays),
        }),
      );
    },
  });

  useEffect(() => {
    setIsTrialDaysUpdating(updateTrialDaysMutation.isPending);
  }, [updateTrialDaysMutation.isPending, setIsTrialDaysUpdating]);

  return updateTrialDaysMutation;
};
